// Pure permission domain — no I/O, no React, no Supabase.
// The single authorization predicate `hasPermission` is consumed by:
//   * apps/erp-web (usePermissions hook → can/route guards/UI gates)
//   * Future backends (Spring Boot will reimplement; same shape)
//
// Module catalog MUST stay in lock-step with the seed migration
// supabase/migrations/20260505000002_bootstrap_system_roles.sql.

export const PERMISSION_MODULES = [
  // Tenant-toggleable modules (mirror ModuleKey from tenant-onboarding)
  'sales',
  'purchases',
  'stock',
  'payments',
  'treasury',
  'checks',
  'withholding',
  'storefront',
  'caisse',
  'pro',
  // Always-on entities (master data)
  'clients',
  'suppliers',
  'articles',
  'representatives',
  // System scopes
  'reports',
  'settings',
  'team',
  'billing',
] as const;

export type PermissionModule = (typeof PERMISSION_MODULES)[number];

export const PERMISSION_ACTIONS = ['view', 'create', 'edit', 'delete'] as const;
export type PermissionAction = (typeof PERMISSION_ACTIONS)[number];

export interface Permission {
  module: string;
  action: PermissionAction;
}

// Minimal shape needed by hasPermission. Full TenantMember (with email,
// avatar, etc.) lives in the data-access interfaces.
export interface MemberWithRole {
  roleSlug: 'owner' | 'admin' | 'member' | string;
  permissions: Permission[];
}

// Owner overrides everything (defense in depth — even if their permissions
// array is somehow empty, they retain full access in their tenant).
export function hasPermission(
  member: MemberWithRole | null | undefined,
  module: string,
  action: PermissionAction,
): boolean {
  if (!member) return false;
  if (member.roleSlug === 'owner') return true;
  return member.permissions.some(
    (p) => p.module === module && p.action === action,
  );
}

// Convenience for sidebar / nav filtering — all modules the member has
// at least 'view' on (or every module if owner).
export function modulesUserCanView(
  member: MemberWithRole | null | undefined,
): string[] {
  if (!member) return [];
  if (member.roleSlug === 'owner') return [...PERMISSION_MODULES];
  const set = new Set<string>();
  for (const p of member.permissions) {
    if (p.action === 'view') set.add(p.module);
  }
  return [...set];
}
